import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Clock, GitBranch, Copy, Zap, RefreshCw, Download, Send,
  Search, ShieldCheck, Pencil, Check, X, AlertCircle,
  ChevronDown, ExternalLink, Pin,
} from 'lucide-react'
import { api } from '@/lib/api/client'
import { Skeleton } from '@/components/ui/skeleton'

// ─── Types ────────────────────────────────────────────────────────────────────

interface AuditScore {
  category: string
  label: string | null
  valueNum: number | null
  unit: string | null
  findingCount: number
}

interface AuditFinding {
  id: number
  agent: string
  severity: string
  status: 'new' | 'existing' | 'resolved'
  filePath: string | null
  lineNumber: number | null
  ruleId: string | null
  title: string
  message: string | null
  fingerprint: string
}

interface AuditRun {
  id: number
  runAt: string
  triggeredBy: string
  branch: string | null
  commitSha: string | null
  durationS: number | null
  overallScore: number | null
  findingCount: number
  newCount: number
  resolvedCount: number
  notes: string | null
  scores: AuditScore[]
  findings?: AuditFinding[]
  resolvedFindings?: AuditFinding[]
  prevRunId?: number | null
}

// ─── Constants ────────────────────────────────────────────────────────────────

const OK_COLOR     = 'oklch(0.66 0.13 150)'
const WARN_COLOR   = 'oklch(0.74 0.14 78)'
const ERR_COLOR    = 'oklch(0.6 0.18 25)'
const FAIR_COLOR   = 'oklch(0.65 0.13 95)'
const MUTED        = 'oklch(0.5 0.012 250)'
const SUBTLE       = 'oklch(0.65 0.01 250)'
const BORDER       = 'oklch(0.92 0.006 220)'
const SURFACE      = 'white'
const SURFACE2     = 'oklch(0.975 0.004 220)'
const SURFACE3     = 'oklch(0.95 0.005 220)'
const BRAND        = 'oklch(0.62 0.07 200)'
const BRAND_SOFT   = 'oklch(0.95 0.025 198)'
const BRAND_STRONG = 'oklch(0.52 0.09 200)'

// ─── Definitions ──────────────────────────────────────────────────────────────

const SCORE_DEFS = [
  { id: 'waf',         label: 'WAF Score',         cats: ['waf_reliability','waf_security','waf_cost','waf_operational','waf_performance'], unit: '/5',   max: 5,   kind: 'grade'   as const, agg: 'avg'   as const },
  { id: 'cis',         label: 'CIS Benchmark',     cats: ['cis_benchmark'],                                                                 unit: '%',    max: 100, kind: 'percent' as const, agg: 'first' as const },
  { id: 'advisor',     label: 'Azure Advisor',     cats: ['advisor_overall'],                                                               unit: '%',    max: 100, kind: 'percent' as const, agg: 'first' as const },
  { id: 'defender',    label: 'Defender Score',    cats: ['defender_secure_score'],                                                         unit: '%',    max: 100, kind: 'percent' as const, agg: 'first' as const },
  { id: 'observatory', label: 'Observatory',       cats: ['observatory'],                                                                   unit: '/100', max: 100, kind: 'count'   as const, agg: 'first' as const },
  { id: 'sast',        label: 'SAST Issues',       cats: ['python_sast','typescript_sast'],                                                 unit: '',     max: null,kind: 'count'   as const, agg: 'sum'   as const },
  { id: 'security',    label: 'Security Findings', cats: ['security_findings'],                                                             unit: '',     max: null,kind: 'count'   as const, agg: 'sum'   as const },
  { id: 'cves',        label: 'Dep CVEs',          cats: ['dep_cves'],                                                                      unit: '',     max: null,kind: 'count'   as const, agg: 'sum'   as const },
] as const

const WAF_DEFS = [
  { cat: 'waf_reliability',  id: 'RE', name: 'Reliability',    notes: 'No geo-redundant backup · no DR runbook' },
  { cat: 'waf_security',     id: 'SE', name: 'Security',       notes: 'Defender 100% · rate limiting gap' },
  { cat: 'waf_cost',         id: 'CO', name: 'Cost Opt.',      notes: 'Budget alerts configured' },
  { cat: 'waf_operational',  id: 'OE', name: 'Op. Excellence', notes: 'CI/CD healthy · stale-time issues' },
  { cat: 'waf_performance',  id: 'PE', name: 'Perf. Efficiency', notes: 'Bundle 1.6MB · no SLO targets' },
] as const

const AGENT_DEFS = [
  { id: 1,  name: 'Security scan',   scoreCats: [] as string[], findingAgent: 'security' },
  { id: 2,  name: 'Dep CVE scan',    scoreCats: ['dep_cves'],   findingAgent: '' },
  { id: 3,  name: 'Infrastructure',  scoreCats: [],             findingAgent: 'infra' },
  { id: 4,  name: 'Database',        scoreCats: [],             findingAgent: 'db' },
  { id: 5,  name: 'Frontend',        scoreCats: [],             findingAgent: 'frontend' },
  { id: 6,  name: 'WAF pillars',     scoreCats: ['waf_reliability','waf_security','waf_cost','waf_operational','waf_performance'], findingAgent: 'waf' },
  { id: 7,  name: 'Python SAST',     scoreCats: ['python_sast'],     findingAgent: 'python_sast' },
  { id: 8,  name: 'TypeScript SAST', scoreCats: ['typescript_sast'], findingAgent: 'typescript_sast' },
  { id: 9,  name: 'CIS Benchmark',   scoreCats: ['cis_benchmark'],   findingAgent: 'iac' },
  { id: 10, name: 'Observatory',     scoreCats: ['observatory'],     findingAgent: '' },
] as const

type Severity = 'critical' | 'high' | 'medium' | 'low'
type Category = 'iac' | 'security' | 'db' | 'frontend' | 'infra' | 'quality'

const SEV_META: Record<Severity, { label: string; bg: string; fg: string; dot: string }> = {
  critical: { label: 'Critical', bg: 'oklch(0.28 0.12 25)',  fg: 'oklch(0.96 0.04 25)',  dot: 'oklch(0.6 0.18 25)'  },
  high:     { label: 'High',     bg: 'oklch(0.58 0.18 25)',  fg: 'white',                dot: 'oklch(0.6 0.18 25)'  },
  medium:   { label: 'Medium',   bg: 'oklch(0.78 0.14 78)',  fg: 'oklch(0.25 0.06 60)',  dot: 'oklch(0.74 0.14 78)' },
  low:      { label: 'Low',      bg: 'oklch(0.88 0.06 250)', fg: 'oklch(0.35 0.06 250)', dot: 'oklch(0.65 0.06 250)' },
}

const CAT_META: Record<Category, { label: string; color: string }> = {
  iac:      { label: 'IaC',      color: 'oklch(0.6 0.14 220)' },
  security: { label: 'Security', color: 'oklch(0.55 0.18 25)'  },
  db:       { label: 'Database', color: 'oklch(0.55 0.13 280)' },
  frontend: { label: 'Frontend', color: 'oklch(0.55 0.14 200)' },
  infra:    { label: 'Infra',    color: 'oklch(0.55 0.13 60)'  },
  quality:  { label: 'Quality',  color: 'oklch(0.55 0.13 145)' },
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getScoreValue(def: typeof SCORE_DEFS[number], scores: AuditScore[]): number | null {
  const nums = scores
    .filter(s => (def.cats as readonly string[]).includes(s.category))
    .map(s => s.valueNum)
    .filter((n): n is number => n !== null)
  if (!nums.length) return null
  if (def.agg === 'avg')  return nums.reduce((a, b) => a + b, 0) / nums.length
  if (def.agg === 'sum')  return nums.reduce((a, b) => a + b, 0)
  return nums[0]
}

function scoreToneColor(value: number | null, kind: 'grade' | 'percent' | 'count', max: number | null): string {
  if (value === null) return SUBTLE
  if (kind === 'count') {
    if (value === 0)  return OK_COLOR
    if (value < 5)    return FAIR_COLOR
    return WARN_COLOR
  }
  const pct = max ? (value / max) * 100 : value
  if (pct >= 90) return OK_COLOR
  if (pct >= 70) return FAIR_COLOR
  if (pct >= 50) return WARN_COLOR
  return ERR_COLOR
}

function deriveCategory(agent: string): Category {
  const a = agent.toLowerCase()
  if (a.includes('frontend'))                                  return 'frontend'
  if (a.includes('database') || a.includes('db'))             return 'db'
  if (a.includes('iac') || a.includes('cis') || a === 'iac')  return 'iac'
  if (a.includes('infra'))                                     return 'infra'
  if (a.includes('security') || a.includes('cve'))            return 'security'
  return 'quality'
}

function normSev(s: string): Severity {
  const l = s.toLowerCase()
  if (l === 'critical') return 'critical'
  if (l === 'high')     return 'high'
  if (l === 'medium')   return 'medium'
  return 'low'
}

function computeOverallScore(run: AuditRun): number {
  if (run.overallScore !== null && run.overallScore !== undefined) return run.overallScore
  const wafCats = ['waf_reliability','waf_security','waf_cost','waf_operational','waf_performance']
  const wafNums = run.scores.filter(s => wafCats.includes(s.category)).map(s => s.valueNum).filter((n): n is number => n !== null)
  const waf = wafNums.length ? wafNums.reduce((a, b) => a + b, 0) / wafNums.length : null
  const cis = run.scores.find(s => s.category === 'cis_benchmark')?.valueNum ?? null
  const adv = run.scores.find(s => s.category === 'advisor_overall')?.valueNum ?? null
  const def = run.scores.find(s => s.category === 'defender_secure_score')?.valueNum ?? null
  const parts: number[] = []
  if (waf !== null) parts.push(waf / 5 * 100 * 0.30)
  if (cis !== null) parts.push(cis * 0.25)
  if (adv !== null) parts.push(adv * 0.25)
  if (def !== null) parts.push(def * 0.20)
  if (!parts.length) return 0
  return Math.round(parts.reduce((a, b) => a + b, 0))
}

function fmtRailDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

function fmtTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })
}

function fmtDuration(s: number | null): string {
  if (s === null) return '—'
  const m = Math.floor(s / 60)
  const sec = s % 60
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`
}

function fmtHeroDate(iso: string): string {
  const d = new Date(iso)
  const day   = d.getDate()
  const month = d.toLocaleDateString('en-GB', { month: 'short' })
  const year  = d.getFullYear()
  const hh    = d.getHours().toString().padStart(2, '0')
  const mm    = d.getMinutes().toString().padStart(2, '0')
  return `${day} ${month} ${year} ${hh}:${mm}`
}

// ─── Sparkline ────────────────────────────────────────────────────────────────

function Sparkline({ data, color = BRAND, height = 28 }: {
  data: (number | null)[]; color?: string; height?: number
}) {
  const nums = data.filter((v): v is number => v !== null)
  if (nums.length < 2) return <svg style={{ width: '100%', height, display: 'block' }} />
  const max = Math.max(...nums); const min = Math.min(...nums)
  const range = Math.max(1, max - min)
  const W = 100
  const points = nums.map((v, i) => {
    const x = (i / (nums.length - 1)) * W
    const y = height - 4 - ((v - min) / range) * (height - 8)
    return `${x},${y}`
  }).join(' ')
  return (
    <svg viewBox={`0 0 ${W} ${height}`} preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }}>
      <polygon points={`0,${height} ${points} ${W},${height}`} fill={color} fillOpacity="0.1" />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

// ─── ScoreRing ────────────────────────────────────────────────────────────────

function ScoreRing({ value, max, size = 56, stroke = 5, color }: {
  value: number | null; max: number | null; size?: number; stroke?: number; color: string
}) {
  const r    = (size - stroke) / 2
  const circ = 2 * Math.PI * r
  const pct  = value !== null && max !== null ? Math.min(value / max, 1) : 0
  const dash = pct * circ
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={SURFACE3} strokeWidth={stroke} />
      {pct > 0 && (
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth={stroke}
          strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={circ / 4}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      )}
    </svg>
  )
}

// ─── ScoreTile ────────────────────────────────────────────────────────────────

function ScoreTile({ def, run, runs }: {
  def: typeof SCORE_DEFS[number]
  run: AuditRun
  runs: AuditRun[]
}) {
  const value   = getScoreValue(def, run.scores)
  const color   = scoreToneColor(value, def.kind, def.max)
  const prevRun = [...runs].sort((a, b) => b.id - a.id).find(r => r.id < run.id)
  const prevVal = prevRun ? getScoreValue(def, prevRun.scores) : null
  const delta   = value !== null && prevVal !== null ? value - prevVal : null
  const history = [...runs].sort((a, b) => a.id - b.id).slice(-10).map(r => getScoreValue(def, r.scores))

  const display = value !== null
    ? (def.kind === 'grade' ? value.toFixed(1) : Math.round(value).toString())
    : '—'

  const isCount   = def.kind === 'count'
  const isErrored = value === null

  // pip color
  const pipColor = isErrored ? ERR_COLOR : color

  // icon for count/errored circle
  const iconName = isErrored ? 'alertCircle'
    : def.id === 'sast' ? 'zap'
    : def.id === 'cves' ? 'lock'
    : 'alert'

  function CircleIcon() {
    if (iconName === 'alertCircle') return <AlertCircle size={20} style={{ color }} />
    if (iconName === 'zap')         return <Zap          size={20} style={{ color }} />
    return                                 <AlertCircle  size={20} style={{ color }} />
  }

  // Delta display
  let deltaEl: React.ReactNode = null
  if (delta !== null && !isErrored) {
    const formatted = def.kind === 'grade' ? delta.toFixed(1) : String(Math.round(delta))
    if (delta === 0) {
      deltaEl = <span className="mono" style={{ fontSize: 11, color: SUBTLE }}>no change</span>
    } else {
      const good = isCount ? delta < 0 : delta > 0
      deltaEl = (
        <span className="mono" style={{ fontSize: 11, fontWeight: 600, color: good ? OK_COLOR : ERR_COLOR }}>
          {delta > 0 ? '+' : ''}{formatted} vs last
        </span>
      )
    }
  }

  return (
    <div style={{
      background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12,
      padding: 14, display: 'flex', flexDirection: 'column', gap: 10,
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Top row: pip + label */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: pipColor, flexShrink: 0 }} />
        <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED }}>
          {def.label}
        </span>
      </div>

      {/* Middle: ring/circle + value */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {!isCount && !isErrored ? (
          <ScoreRing value={value} max={def.max} size={56} stroke={5} color={color} />
        ) : (
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: SURFACE2, border: `1px solid ${BORDER}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <CircleIcon />
          </div>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
            <span style={{
              fontFamily: 'JetBrains Mono, monospace', fontSize: 28, fontWeight: 700,
              letterSpacing: '-0.02em', lineHeight: 1, color,
            }}>
              {display}
            </span>
            {def.unit && (
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, fontWeight: 500, color: MUTED }}>
                {def.unit}
              </span>
            )}
          </div>
          {deltaEl && (
            <div style={{ marginTop: 4 }}>{deltaEl}</div>
          )}
        </div>
      </div>

      {/* Bottom: sparkline + note */}
      <div style={{ marginTop: -4 }}>
        <div style={{ height: 22, opacity: 0.75 }}>
          <Sparkline data={history} color={color} height={22} />
        </div>
        <div style={{ fontSize: 10.5, color: MUTED, marginTop: 4, lineHeight: 1.35, minHeight: 14 }} />
      </div>
    </div>
  )
}

// ─── WafPillars ───────────────────────────────────────────────────────────────

function WafPillars({ scores }: { scores: AuditScore[] }) {
  const wafVals = WAF_DEFS.map(d => scores.find(s => s.category === d.cat)?.valueNum).filter((v): v is number => v !== null)
  const overall = wafVals.length ? wafVals.reduce((a, b) => a + b, 0) / wafVals.length : null

  return (
    <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13.5 }}>Well-Architected Framework pillars</div>
          <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>Microsoft WAF · scored 0–5 per pillar · weighted average → WAF score</div>
        </div>
        {overall !== null && (
          <span style={{
            display: 'inline-flex', alignItems: 'center',
            fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5, fontWeight: 600,
            padding: '2px 8px', borderRadius: 999,
            background: `${BRAND}26`, color: BRAND,
          }}>
            {overall.toFixed(1)} / 5 overall
          </span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
        {WAF_DEFS.map(d => {
          const val   = scores.find(s => s.category === d.cat)?.valueNum ?? null
          const pct   = val !== null ? (val / 5) * 100 : 0
          const color = val !== null ? scoreToneColor(val, 'grade', 5) : SUBTLE
          return (
            <div key={d.id}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 700, letterSpacing: '-0.02em', color: MUTED }}>{d.id}</span>
                  <span style={{ marginLeft: 6, fontSize: 12.5, fontWeight: 500 }}>{d.name}</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div style={{ flex: 1, height: 6, background: SURFACE3, borderRadius: 999, overflow: 'hidden', position: 'relative' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 999 }} />
                  {/* Target tick at 80% = 4.0/5 */}
                  <div style={{ position: 'absolute', left: '80%', top: -2, bottom: -2, width: 1.5, background: SUBTLE, opacity: 0.5 }} />
                </div>
                <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, fontWeight: 700, color, minWidth: 28, textAlign: 'right' }}>
                  {val !== null ? val.toFixed(1) : '—'}
                </span>
              </div>
              <div style={{ fontSize: 10.5, color: MUTED, lineHeight: 1.4 }}>{d.notes}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── SeverityBreakdown ────────────────────────────────────────────────────────

function SeverityBreakdown({
  findings, filterSeverity, setFilterSeverity,
}: {
  findings: AuditFinding[]
  filterSeverity: Severity | null
  setFilterSeverity: (s: Severity | null) => void
}) {
  const counts = { critical: 0, high: 0, medium: 0, low: 0 } as Record<Severity, number>
  for (const f of findings) counts[normSev(f.severity)]++
  const total = Object.values(counts).reduce((a, b) => a + b, 0)
  const order: Severity[] = ['critical', 'high', 'medium', 'low']

  return (
    <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13.5 }}>Findings breakdown</div>
          <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>{total} total · click to filter</div>
        </div>
      </div>

      {/* Stacked bar */}
      <div style={{ display: 'flex', height: 12, borderRadius: 999, gap: 2, overflow: 'visible', marginBottom: 12 }}>
        {order.map(sev => {
          const c = counts[sev]
          if (c === 0) return null
          const m = SEV_META[sev]
          return (
            <div key={sev}
              title={`${m.label}: ${c}`}
              onClick={() => setFilterSeverity(filterSeverity === sev ? null : sev)}
              style={{
                flex: c, background: m.bg, cursor: 'pointer', borderRadius: 4,
                transition: 'opacity 0.15s',
                opacity: !filterSeverity || filterSeverity === sev ? 1 : 0.3,
              }}
            />
          )
        })}
        {total === 0 && <div style={{ flex: 1, background: SURFACE2, borderRadius: 999 }} />}
      </div>

      {/* 2×2 buttons */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {order.map(sev => {
          const c      = counts[sev]
          const m      = SEV_META[sev]
          const active = filterSeverity === sev
          return (
            <button key={sev}
              onClick={() => setFilterSeverity(active ? null : sev)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 10px', borderRadius: 6, cursor: 'pointer', textAlign: 'left',
                border: `1px solid ${active ? m.bg : BORDER}`,
                background: active ? `${m.bg}14` : SURFACE,
              }}
            >
              <SeverityPill sev={sev} />
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 16, fontWeight: 700, color: c > 0 ? 'var(--ns-text)' : SUBTLE }}>
                {c}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ─── Pills ────────────────────────────────────────────────────────────────────

function SeverityPill({ sev }: { sev: Severity }) {
  const m = SEV_META[sev]
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em',
      padding: '3px 7px', borderRadius: 3,
      background: m.bg, color: m.fg,
      fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'nowrap',
    }}>
      {m.label.toUpperCase()}
    </span>
  )
}

function CategoryPill({ cat }: { cat: Category }) {
  const m = CAT_META[cat]
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: m.color, display: 'inline-block', flexShrink: 0 }} />
      <span style={{ fontSize: 11.5, fontWeight: 500, color: MUTED }}>{m.label}</span>
    </span>
  )
}

function NewPill() {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
      padding: '2px 5px', borderRadius: 3,
      background: OK_COLOR, color: 'white',
      fontFamily: 'JetBrains Mono, monospace',
    }}>
      NEW
    </span>
  )
}

// ─── AgentsStrip ──────────────────────────────────────────────────────────────

function AgentsStrip({ scores, findings }: { scores: AuditScore[]; findings: AuditFinding[] }) {
  // Derive per-agent status
  const agentData = AGENT_DEFS.map(a => {
    // err: has CRITICAL finding from this agent's findingAgent
    const hasErr = a.findingAgent
      ? findings.some(f => f.agent.toLowerCase().includes(a.findingAgent) && normSev(f.severity) === 'critical')
      : false

    // warn: any score cat is null
    const hasScoreCats = a.scoreCats.length > 0
    const anyNull = hasScoreCats
      ? a.scoreCats.some(cat => {
          const sc = scores.find(s => s.category === cat)
          return !sc || sc.valueNum === null
        })
      : false

    const status: 'ok' | 'warn' | 'err' = hasErr ? 'err' : anyNull ? 'warn' : 'ok'

    // mock duration: 4–8 min per agent
    const duration = 240 + (a.id * 37) % 240

    return { ...a, status, duration }
  })

  const okCount   = agentData.filter(a => a.status === 'ok').length
  const warnCount = agentData.filter(a => a.status === 'warn').length
  const errCount  = agentData.filter(a => a.status === 'err').length
  const totalSecs = agentData.reduce((s, a) => s + a.duration, 0)
  const totalMins = Math.floor(totalSecs / 60)

  return (
    <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Audit agents · this run</div>
          <div style={{ fontSize: 11, color: MUTED, marginTop: 1 }}>10 specialised agents ran in parallel</div>
        </div>
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: MUTED }}>
          {totalMins}m total · {okCount} ok · {warnCount} warn · {errCount} err
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 6 }}>
        {agentData.map(a => {
          const color = a.status === 'ok' ? OK_COLOR : a.status === 'warn' ? WARN_COLOR : ERR_COLOR
          const mins  = Math.floor(a.duration / 60)
          const secs  = (a.duration % 60).toString().padStart(2, '0')
          return (
            <div key={a.id}
              title={`Agent A${a.id.toString().padStart(2,'0')} — ${a.name}`}
              style={{
                padding: '8px 6px', borderRadius: 6,
                background: SURFACE2, border: `1px solid ${BORDER}`,
                position: 'relative', overflow: 'hidden',
              }}
            >
              {/* 3px left-edge status bar */}
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: color }} />
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, fontWeight: 700, color: SUBTLE, marginLeft: 4 }}>
                A{a.id.toString().padStart(2, '0')}
              </div>
              <div style={{ fontSize: 11, fontWeight: 500, marginLeft: 4, marginTop: 1, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {a.name}
              </div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9.5, color: SUBTLE, marginLeft: 4, marginTop: 2 }}>
                {mins}m {secs}s
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── FindingRow ───────────────────────────────────────────────────────────────

function FindingRow({ f, expanded, onToggle }: {
  f: AuditFinding
  expanded: boolean
  onToggle: () => void
}) {
  const sev = normSev(f.severity)
  const cat = deriveCategory(f.agent)

  return (
    <>
      <tr
        onClick={onToggle}
        style={{ cursor: 'pointer', background: expanded ? SURFACE2 : undefined }}
        className="hover:[&>td]:bg-[oklch(0.975_0.004_220)]"
      >
        {/* Severity */}
        <td style={{ padding: '9px 8px 9px 14px', borderBottom: `1px solid ${BORDER}`, width: 80, whiteSpace: 'nowrap' }}>
          <SeverityPill sev={sev} />
        </td>
        {/* Category */}
        <td style={{ padding: '9px 8px', borderBottom: `1px solid ${BORDER}`, width: 90 }}>
          <CategoryPill cat={cat} />
        </td>
        {/* Title + file */}
        <td style={{ padding: '9px 8px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ fontWeight: 500, fontSize: 13 }}>{f.title}</div>
          {f.filePath && (
            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: SUBTLE, marginTop: 2 }}>
              {f.filePath}{f.lineNumber ? `:${f.lineNumber}` : ''}
            </div>
          )}
        </td>
        {/* NEW badge */}
        <td style={{ padding: '9px 8px', borderBottom: `1px solid ${BORDER}`, width: 80 }}>
          {f.status === 'new' && <NewPill />}
        </td>
        {/* Agent */}
        <td style={{ padding: '9px 8px', borderBottom: `1px solid ${BORDER}`, width: 80 }}>
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5, color: MUTED }}>{f.agent}</span>
        </td>
        {/* Rule code chip */}
        <td style={{ padding: '9px 8px', borderBottom: `1px solid ${BORDER}`, width: 200 }}>
          {f.ruleId && (
            <span style={{
              fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5, color: MUTED,
              background: SURFACE2, border: `1px solid ${BORDER}`, borderRadius: 3,
              padding: '2px 6px',
            }}>
              {f.ruleId}
            </span>
          )}
        </td>
        {/* Chevron */}
        <td style={{ padding: '9px 8px 9px 4px', borderBottom: `1px solid ${BORDER}`, width: 32 }}>
          <ChevronDown size={12} style={{
            color: SUBTLE,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
            display: 'block',
          }} />
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} style={{ padding: 0, background: SURFACE2 }}>
            <div style={{ padding: '14px 18px 16px 100px', display: 'flex', gap: 24, fontSize: 12.5 }}>
              <div style={{ flex: 1, maxWidth: 720 }}>
                <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>
                  Recommendation
                </div>
                <div style={{ lineHeight: 1.55 }}>
                  {f.message ?? 'No additional guidance.'}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, alignSelf: 'flex-end' }}>
                <button style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                  border: `1px solid ${BORDER}`, background: SURFACE, color: MUTED,
                }}>
                  <ExternalLink size={12} />Open in editor
                </button>
                <button style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                  border: `1px solid ${BORDER}`, background: SURFACE, color: MUTED,
                }}>
                  <Pin size={12} />Snooze
                </button>
                <button style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                  border: `1px solid ${BRAND}`, background: BRAND, color: 'white',
                }}>
                  <Check size={12} />Mark resolved
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// ─── FindingsTable ────────────────────────────────────────────────────────────

type FindingTab = 'all' | 'new' | 'existing' | 'resolved'

function FindingsTable({ run, filterSeverity, setFilterSeverity }: {
  run: AuditRun
  filterSeverity: Severity | null
  setFilterSeverity: (s: Severity | null) => void
}) {
  const [tab,       setTab]       = useState<FindingTab>('all')
  const [catFilter, setCatFilter] = useState<Category | null>(null)
  const [agentFilter, setAgent]   = useState<string>('')
  const [search,    setSearch]    = useState('')
  const [expanded,  setExpanded]  = useState<number | null>(null)

  const allFindings  = run.findings         ?? []
  const resolvedOnes = run.resolvedFindings ?? []
  const source       = tab === 'resolved' ? resolvedOnes : allFindings

  const agents = useMemo(() => [...new Set(allFindings.map(f => f.agent))].sort(), [allFindings])

  const filtered = useMemo(() => source.filter(f => {
    if (tab === 'new'      && f.status !== 'new')      return false
    if (tab === 'existing' && f.status !== 'existing') return false
    if (filterSeverity && normSev(f.severity) !== filterSeverity) return false
    if (catFilter && deriveCategory(f.agent) !== catFilter)        return false
    if (agentFilter && f.agent !== agentFilter)                    return false
    if (search && !f.title.toLowerCase().includes(search.toLowerCase()) &&
        !(f.filePath ?? '').toLowerCase().includes(search.toLowerCase())) return false
    return true
  }), [source, tab, filterSeverity, catFilter, agentFilter, search])

  const newCount      = allFindings.filter(f => f.status === 'new').length
  const existingCount = allFindings.filter(f => f.status === 'existing').length

  const TABS: { id: FindingTab; label: string; count: number }[] = [
    { id: 'all',      label: 'All',      count: allFindings.length },
    { id: 'new',      label: 'New',      count: newCount },
    { id: 'existing', label: 'Existing', count: existingCount },
    { id: 'resolved', label: 'Resolved', count: resolvedOnes.length },
  ]

  const CATS: Category[] = ['iac', 'security', 'db', 'frontend', 'infra', 'quality']

  return (
    <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12, overflow: 'hidden' }}>
      {/* Header bar */}
      <div style={{ padding: '12px 14px', borderBottom: `1px solid ${BORDER}` }}>
        {/* Top row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>Findings</div>
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>
              {filtered.length} of {allFindings.length} shown
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
              border: `1px solid ${BORDER}`, background: SURFACE, color: MUTED,
            }}>
              <Download size={12} />Export CSV
            </button>
            <button style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
              border: `1px solid ${BORDER}`, background: SURFACE, color: MUTED,
            }}>
              <Send size={12} />Open as work items
            </button>
          </div>
        </div>

        {/* Sub-tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{
                padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 500,
                background: tab === t.id ? BRAND_SOFT : 'transparent',
                color: tab === t.id ? BRAND_STRONG : MUTED,
                border: 'none', cursor: 'pointer', textTransform: 'capitalize',
              }}
            >
              {t.label}
              <span style={{ fontFamily: 'JetBrains Mono, monospace', marginLeft: 4, opacity: 0.7 }}>{t.count}</span>
            </button>
          ))}
        </div>

        {/* Filter row */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Search */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: 'none' }}>
            <Search size={13} style={{ position: 'absolute', left: 7, color: SUBTLE, pointerEvents: 'none' }} />
            <input
              placeholder="rule, file, message…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: 240, height: 26, paddingLeft: 24, paddingRight: 8, fontSize: 12,
                border: `1px solid ${BORDER}`, borderRadius: 6, background: SURFACE,
                color: 'oklch(0.21 0.012 250)', outline: 'none',
              }}
            />
          </div>

          {/* Category chips */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: MUTED, marginRight: 2 }}>Category:</span>
            <button
              onClick={() => setCatFilter(null)}
              style={{
                height: 24, padding: '0 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                border: `1px solid ${catFilter === null ? BRAND : BORDER}`,
                background: catFilter === null ? BRAND_SOFT : SURFACE,
                color: catFilter === null ? BRAND_STRONG : MUTED,
              }}
            >
              All
            </button>
            {CATS.map(cat => {
              const m     = CAT_META[cat]
              const count = allFindings.filter(f => deriveCategory(f.agent) === cat).length
              if (count === 0) return null
              const active = catFilter === cat
              return (
                <button key={cat}
                  onClick={() => setCatFilter(active ? null : cat)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    height: 24, padding: '0 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                    border: `1px solid ${active ? m.color : BORDER}`,
                    background: active ? `${m.color}18` : SURFACE,
                    color: active ? m.color : MUTED,
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: 1.5, background: m.color, display: 'inline-block' }} />
                  {m.label}
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', opacity: 0.6 }}>{count}</span>
                </button>
              )
            })}
          </div>

          {/* Agent select */}
          <select
            value={agentFilter}
            onChange={e => setAgent(e.target.value)}
            style={{
              height: 26, fontSize: 11.5, width: 'auto', minWidth: 100,
              border: `1px solid ${BORDER}`, borderRadius: 6, padding: '0 8px',
              background: SURFACE, color: 'oklch(0.21 0.012 250)',
            }}
          >
            <option value="">All agents</option>
            {agents.map(a => <option key={a} value={a}>{a}</option>)}
          </select>

          {/* Active severity chip */}
          {filterSeverity && (
            <button
              onClick={() => setFilterSeverity(null)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                height: 24, padding: '0 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                border: `1px solid ${SEV_META[filterSeverity].bg}`,
                background: `${SEV_META[filterSeverity].bg}14`,
                color: MUTED,
              }}
            >
              Severity: {SEV_META[filterSeverity].label}
              <span style={{ marginLeft: 2 }}>×</span>
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div style={{ padding: '32px 24px', textAlign: 'center', fontSize: 12.5, color: MUTED }}>
          {tab === 'resolved' && resolvedOnes.length === 0
            ? 'No resolved findings yet — run the audit again after applying fixes.'
            : 'No findings match your filters.'}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {filtered.map(f => (
              <FindingRow
                key={`${f.status}-${f.id}`}
                f={f}
                expanded={expanded === f.id}
                onToggle={() => setExpanded(expanded === f.id ? null : f.id)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ─── NotesEditor ──────────────────────────────────────────────────────────────

function NotesEditor({ runId, initial }: { runId: number; initial: string | null }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft,   setDraft]   = useState(initial ?? '')

  const save = useMutation({
    mutationFn: (notes: string) => api.patch<{ id: number }>(`/audit/runs/${runId}`, { notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['audit-runs'] })
      qc.invalidateQueries({ queryKey: ['audit-run', runId] })
      setEditing(false)
    },
  })

  if (!editing) {
    return (
      <div className="group flex items-start gap-2">
        {initial ? (
          <p style={{ fontSize: 12, lineHeight: 1.55, flex: 1, margin: 0, opacity: 0.85 }}>{initial}</p>
        ) : (
          <p style={{ fontSize: 12, opacity: 0.6, fontStyle: 'italic', flex: 1, margin: 0 }}>No notes for this run.</p>
        )}
        <button
          onClick={() => { setDraft(initial ?? ''); setEditing(true) }}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded"
          style={{ flexShrink: 0 }}
        >
          <Pencil size={11} style={{ color: 'oklch(0.7 0.14 145)' }} />
        </button>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <textarea
        value={draft}
        onChange={e => setDraft(e.target.value)}
        rows={2}
        autoFocus
        placeholder="Run note (e.g. 'post batch-3 remediation')"
        style={{
          fontSize: 12, background: 'oklch(0.18 0.02 220 / 0.3)', border: '1px solid oklch(0.4 0.04 220)',
          borderRadius: 6, padding: '6px 10px', color: 'oklch(0.95 0.01 220)',
          resize: 'none', width: '100%', outline: 'none', fontFamily: 'inherit',
        }}
      />
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          onClick={() => save.mutate(draft)}
          disabled={save.isPending}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5,
            padding: '3px 10px', borderRadius: 6, cursor: 'pointer',
            background: 'oklch(0.21 0.012 250)', color: 'white', border: 'none',
          }}
        >
          <Check size={10} />
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={() => setEditing(false)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5,
            padding: '3px 10px', borderRadius: 6, cursor: 'pointer',
            background: 'none', border: '1px solid oklch(0.4 0.04 220)', color: 'oklch(0.75 0.02 220)',
          }}
        >
          <X size={10} />Cancel
        </button>
      </div>
    </div>
  )
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

function Hero({ run, runs, findings }: { run: AuditRun; runs: AuditRun[]; findings: AuditFinding[] }) {
  const score = computeOverallScore(run)

  // Hero ring
  const heroR    = 60
  const heroSW   = 10
  const heroCirc = 2 * Math.PI * heroR
  const heroDash = (score / 100) * heroCirc
  const heroColor = score >= 80 ? 'oklch(0.75 0.14 145)' : score >= 60 ? 'oklch(0.85 0.14 78)' : 'oklch(0.75 0.18 25)'

  // Delta vs previous run
  const prevRun   = [...runs].sort((a, b) => b.id - a.id).find(r => r.id < run.id)
  const prevScore = prevRun ? computeOverallScore(prevRun) : null
  const delta     = prevScore !== null ? score - prevScore : null

  // Severity counts
  const sevCounts = { critical: 0, high: 0, medium: 0, low: 0 } as Record<Severity, number>
  for (const f of findings) sevCounts[normSev(f.severity)]++

  const sevBricks = [
    { label: 'CRIT', sev: 'critical' as Severity },
    { label: 'HIGH', sev: 'high'     as Severity },
    { label: 'MED',  sev: 'medium'   as Severity },
    { label: 'LOW',  sev: 'low'      as Severity },
  ]

  return (
    <div style={{
      background: 'linear-gradient(135deg, oklch(0.32 0.04 200) 0%, oklch(0.22 0.02 230) 100%)',
      color: 'oklch(0.95 0.01 220)',
      border: '1px solid oklch(0.4 0.04 220)',
      padding: '20px 22px',
      borderRadius: 12,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 24, alignItems: 'flex-start' }}>
        {/* Left */}
        <div>
          {/* Status pill + run ID */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{
              fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em',
              padding: '2px 7px', borderRadius: 3,
              background: 'oklch(0.7 0.14 145)', color: 'oklch(0.18 0.05 145)',
            }}>
              COMPLETED
            </span>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5, opacity: 0.7 }}>
              #{run.id}
            </span>
          </div>

          {/* Title */}
          <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em', margin: '0 0 4px' }}>
            Audit run · {fmtHeroDate(run.runAt)}
          </h1>

          {/* Subtitle */}
          <div style={{ fontSize: 13, opacity: 0.85, maxWidth: 720, lineHeight: 1.5 }}>
            Security, quality and compliance scores across 10 agents — tracked per run, per branch.
          </div>

          {/* Meta row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 14, flexWrap: 'wrap', fontSize: 11.5 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Clock size={12} style={{ opacity: 0.6 }} />
              <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{fmtDuration(run.durationS)}</span>
            </div>
            {run.branch && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <GitBranch size={12} style={{ opacity: 0.6 }} />
                <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{run.branch.replace('refs/heads/', '')}</span>
              </div>
            )}
            {run.commitSha && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Copy size={12} style={{ opacity: 0.6 }} />
                <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{run.commitSha.slice(0, 7)}</span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Zap size={12} style={{ opacity: 0.6 }} />
              <span>triggered by <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{run.triggeredBy}</span></span>
            </div>
          </div>

          {/* Notes callout */}
          <div style={{
            marginTop: 14, padding: '10px 12px',
            background: 'oklch(0.18 0.02 220 / 0.5)', borderRadius: 8,
            borderLeft: '3px solid oklch(0.7 0.14 75)',
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', opacity: 0.7, marginBottom: 3 }}>
              Run Notes
            </div>
            <NotesEditor runId={run.id} initial={run.notes} />
          </div>
        </div>

        {/* Right: score ring */}
        <div style={{ textAlign: 'center', minWidth: 160 }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <svg width="140" height="140" viewBox="0 0 140 140">
              <circle cx="70" cy="70" r={heroR} fill="none" stroke="oklch(0.3 0.02 220)" strokeWidth={heroSW} />
              <circle cx="70" cy="70" r={heroR} fill="none"
                stroke={heroColor} strokeWidth={heroSW}
                strokeLinecap="round"
                strokeDasharray={`${heroDash} ${heroCirc - heroDash}`}
                transform="rotate(-90 70 70)"
              />
            </svg>
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 38, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1,
              }}>
                {score}
              </span>
              <span style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>/ 100</span>
            </div>
          </div>

          {/* Delta */}
          {delta !== null && (
            <div style={{ marginTop: 6, fontSize: 11.5, opacity: 0.75 }}>
              <span style={{ fontWeight: 600, color: delta < 0 ? 'oklch(0.85 0.12 25)' : 'oklch(0.75 0.14 145)' }}>
                {delta > 0 ? '+' : ''}{delta}
              </span>
              {' '}vs last run
            </div>
          )}

          {/* Severity bricks */}
          <div style={{ display: 'flex', gap: 4, marginTop: 14, justifyContent: 'center' }}>
            {sevBricks.map(b => {
              const m = SEV_META[b.sev]
              return (
                <div key={b.label} style={{
                  background: m.bg, color: m.fg, borderRadius: 4, padding: '4px 8px',
                  fontFamily: 'JetBrains Mono, monospace', fontSize: 9.5, fontWeight: 700,
                  letterSpacing: '0.06em', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', minWidth: 36,
                }}>
                  {b.label}
                  <span style={{ fontSize: 13, marginTop: 1 }}>{sevCounts[b.sev]}</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── RunHistoryRail ───────────────────────────────────────────────────────────

function RunHistoryRail({
  runs, selectedId, onSelect,
}: {
  runs: AuditRun[]
  selectedId: number | null
  onSelect: (id: number) => void
}) {
  const overallHistory = [...runs].reverse().map(r => computeOverallScore(r))
  const currentScore   = runs.length > 0 ? computeOverallScore(runs[0]) : null

  return (
    <div style={{
      background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12,
      padding: 16, height: '100%', display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <div style={{ fontWeight: 600, fontSize: 13.5 }}>Run history</div>
        <button
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 26, height: 26, borderRadius: 6, cursor: 'pointer',
            border: `1px solid ${BORDER}`, background: SURFACE,
          }}
          title="Refresh"
        >
          <RefreshCw size={12} style={{ color: MUTED }} />
        </button>
      </div>

      {/* Subtitle */}
      <div style={{ fontSize: 11, color: MUTED, marginBottom: 14 }}>Last 10 runs · score &amp; findings</div>

      {/* Score sub-card */}
      <div style={{ background: SURFACE2, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 10, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: MUTED }}>Overall score · 30d</span>
          {currentScore !== null && (
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, fontWeight: 600 }}>
              {currentScore} / 100
            </span>
          )}
        </div>
        <div style={{ height: 28 }}>
          <Sparkline data={overallHistory} color={BRAND_STRONG} height={28} />
        </div>
      </div>

      {/* Timeline */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
        {/* Vertical line */}
        <div style={{ position: 'absolute', left: 10, top: 6, bottom: 6, width: 1, background: BORDER }} />

        {runs.map(run => {
          const isCurrent  = selectedId === run.id
          const findingCnt = run.findingCount
          const dotColor   = findingCnt >= 10 ? ERR_COLOR : findingCnt > 0 ? WARN_COLOR : OK_COLOR

          // Compute delta vs next run (runs[idx+1] is older)
          const idx   = runs.indexOf(run)
          const older = runs[idx + 1]
          const prevFinding = older ? older.findingCount : null
          const delta = prevFinding !== null ? findingCnt - prevFinding : null

          return (
            <div key={run.id}
              onClick={() => onSelect(run.id)}
              style={{
                position: 'relative', paddingLeft: 24, paddingRight: 8, paddingTop: 8, paddingBottom: 8,
                borderRadius: 6, cursor: 'pointer',
                background: isCurrent ? BRAND_SOFT : 'transparent',
              }}
              className={isCurrent ? '' : 'hover:bg-[oklch(0.975_0.004_220)]'}
            >
              {/* Timeline dot */}
              <span style={{
                position: 'absolute', left: 6, top: 13,
                width: 9, height: 9, borderRadius: '50%',
                background: isCurrent ? BRAND_STRONG : SURFACE,
                border: isCurrent ? `2px solid ${SURFACE}` : `2px solid ${dotColor}`,
                boxShadow: isCurrent ? `0 0 0 3px ${BRAND_SOFT}` : 'none',
              }} />

              {/* Line 1 */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{
                  fontSize: 12,
                  fontWeight: isCurrent ? 600 : 500,
                  color: isCurrent ? BRAND_STRONG : 'var(--ns-text, oklch(0.21 0.012 250))',
                }}>
                  {fmtRailDate(run.runAt)} · {fmtTime(run.runAt)}
                </span>
                <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 600, color: findingCnt === 0 ? OK_COLOR : MUTED }}>
                  {findingCnt === 0 ? '✓' : findingCnt}
                  {delta !== null && delta !== 0 && (
                    <span style={{ color: delta > 0 ? ERR_COLOR : OK_COLOR }}>
                      {' '}({delta > 0 ? '+' : ''}{delta})
                    </span>
                  )}
                </span>
              </div>

              {/* Line 2 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2, fontSize: 10.5, color: SUBTLE }}>
                {run.commitSha && (
                  <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{run.commitSha.slice(0, 7)}</span>
                )}
                {run.commitSha && run.branch && <span>·</span>}
                {run.branch && (
                  <span style={{
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    flex: 1, minWidth: 0,
                  }} title={run.branch}>
                    {run.branch.replace('refs/heads/', '')}
                  </span>
                )}
              </div>
            </div>
          )
        })}

        {runs.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 12, color: MUTED }}>
            No runs yet
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AuditPage() {
  const [selectedId,     setSelectedId]     = useState<number | null>(null)
  const [filterSeverity, setFilterSeverity] = useState<Severity | null>(null)

  const { data: runs, isLoading: runsLoading } = useQuery<AuditRun[]>({
    queryKey: ['audit-runs'],
    queryFn:  () => api.get<AuditRun[]>('/audit/runs'),
    staleTime: 60_000,
  })

  // Auto-select the latest run on first load
  useEffect(() => {
    if (runs && runs.length > 0 && selectedId === null) {
      setSelectedId(runs[0].id)
    }
  }, [runs, selectedId])

  const effectiveId = selectedId ?? (runs?.[0]?.id ?? null)

  const { data: runDetail, isLoading: detailLoading } = useQuery<AuditRun>({
    queryKey: ['audit-run', effectiveId],
    queryFn:  () => api.get<AuditRun>(`/audit/runs/${effectiveId}`),
    enabled:  effectiveId !== null,
    staleTime: 5 * 60_000,
  })

  const run = runDetail ?? (runs?.find(r => r.id === effectiveId) ?? null)

  return (
    <div style={{ height: '100%', overflowY: 'auto', background: SURFACE2 }}>
      <div style={{
        padding: 24,
        display: 'grid',
        gridTemplateColumns: '260px 1fr',
        gap: 18,
        alignItems: 'start',
      }}>
        {/* Left: Run history rail (sticky) */}
        <div style={{ position: 'sticky', top: 24, maxHeight: 'calc(100vh - 96px)' }}>
          {runsLoading ? (
            <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : !runs || runs.length === 0 ? (
            <div style={{
              background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12,
              padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 8,
            }}>
              <ShieldCheck size={28} style={{ color: SUBTLE }} />
              <span style={{ fontSize: 12, color: MUTED, textAlign: 'center', lineHeight: 1.55 }}>
                No audit runs yet.<br />
                Run <code style={{ fontSize: 11, background: BORDER, padding: '1px 4px', borderRadius: 3 }}>/forge-audit</code>
              </span>
            </div>
          ) : (
            <RunHistoryRail
              runs={runs}
              selectedId={effectiveId}
              onSelect={setSelectedId}
            />
          )}
        </div>

        {/* Right: Main content */}
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14, paddingBottom: 60 }}>
          {/* Loading skeleton */}
          {(detailLoading || (runsLoading && !run)) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Skeleton className="h-48 w-full rounded-xl" />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-32 w-full rounded-xl" />)}
              </div>
              <Skeleton className="h-64 w-full rounded-xl" />
            </div>
          )}

          {!run && !detailLoading && !runsLoading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, color: MUTED, fontSize: 13 }}>
              Select a run from the left panel
            </div>
          )}

          {run && (
            <>
              {/* 1. Hero */}
              <Hero run={run} runs={runs ?? []} findings={runDetail?.findings ?? []} />

              {/* 2. Score grid — repeat(4, 1fr) */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                {SCORE_DEFS.map(def => (
                  <ScoreTile key={def.id} def={def} run={run} runs={runs ?? []} />
                ))}
              </div>

              {/* 3. WAF + Breakdown */}
              <div style={{ display: 'grid', gridTemplateColumns: '2.1fr 1fr', gap: 12 }}>
                <WafPillars scores={run.scores} />
                <SeverityBreakdown
                  findings={runDetail?.findings ?? []}
                  filterSeverity={filterSeverity}
                  setFilterSeverity={setFilterSeverity}
                />
              </div>

              {/* 4. Agents Strip (NEW) */}
              <AgentsStrip
                scores={run.scores}
                findings={runDetail?.findings ?? []}
              />

              {/* 5. Findings table */}
              {runDetail ? (
                <FindingsTable
                  run={runDetail}
                  filterSeverity={filterSeverity}
                  setFilterSeverity={setFilterSeverity}
                />
              ) : (
                <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: '40px 24px', textAlign: 'center' }}>
                  <div style={{ fontSize: 13, color: MUTED }}>Loading findings…</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
